import { PermissionValues as Permissions } from '@gripp/shared-logic';
import { assetSubRoutes } from '../assets/assetSubRoutes';
import { AssetDetailPanel } from '../assets/components/assetDetailPanel';
import { Assets } from '../assets/components/assets';
import { WorkspaceDuplicatePage } from './components/workspaceDuplicatePage';
import { WorkspaceEdit } from './components/workspaceEdit';
import { WorkspaceNewPage } from './components/workspaceNewPage';
import { Workspaces } from './components/workspaces';
import { WorkspacesFilterPage } from './components/workspacesFilterPage';
import { WorkspacesToolbar } from './components/workspacesToolbar';

export const workspacePaths = {
  root: 'workspaces',
};

export const workspaceRoute = [
  {
    path: `${workspacePaths.root}/*`,
    element: <Workspaces />,
    handle: {
      header: {
        titleKey: 'admin.menu.workspaces',
        icon: 'group',
      },
      requiredPermission: Permissions.workspace.all,
      toolbar: <WorkspacesToolbar />,
    },
  },
  {
    path: `${workspacePaths.root}/new`,
    element: <WorkspaceNewPage />,
    handle: {
      header: {
        titleKey: 'admin.menu.workspaces',
        icon: 'group',
      },
      requiredPermission: Permissions.workspace.create,
    },
  },
  {
    path: `${workspacePaths.root}/:workspaceId/duplicate`,
    element: <WorkspaceDuplicatePage />,
    handle: {
      header: {
        titleKey: 'admin.menu.workspaces',
        icon: 'group',
      },
      requiredPermission: Permissions.globalAdmin,
    },
  },
  {
    path: `${workspacePaths.root}/filters`,
    element: <WorkspacesFilterPage />,
    handle: {
      header: {
        titleKey: 'admin.menu.workspaces',
        icon: 'group',
      },
      requiredPermission: Permissions.workspace.all,
    },
  },
  {
    path: `${workspacePaths.root}/:workspaceId/edit`,
    element: <WorkspaceEdit />,
    handle: {
      header: {
        titleKey: 'workspace.editTitle',
        icon: 'group',
      },
      requiredPermission: Permissions.globalAdmin,
    },
  },
  // The bare asset URL under this prefix. Without it the URL matches nothing
  // and React Router falls through to the `workspaces/*` splat above, whose
  // splat param then reads `:workspaceId/asset/:assetId` — no workspace
  // resolves from that, so the visitor lands on the admin list of every
  // workspace in the system.
  //
  // Mirrors what React Router actually matches for the bare `/assets/:assetId`:
  // the nested `assets` -> `:assetId` pair, NOT one of the flat entries in
  // assets/routes.tsx. `AssetDetailPanel` registers its column through the
  // parent's outlet context and renders nothing on its own, so the section
  // shell has to come with it — hence a layout route rather than a flat entry.
  // The layout route carries no path of its own so that only the one URL
  // becomes matchable; `/workspaces/:workspaceId/asset` is not a page.
  //
  // `asset.read`, not the `globalAdmin` the sub-routes below tighten to: this
  // is where a member who joined an asset through a share link lands, and they
  // are Restricted. That is the gate `/assets/:assetId` already carries, and
  // the same accounts reach the same component — `AssetDetailPanel`,
  // `AssetCard` and `AssetChatHeaderButton` each compute their own guest
  // allowlist, because they already have to for the bare URL under `/assets`.
  {
    element: <Assets />,
    children: [
      {
        path: `${workspacePaths.root}/:workspaceId/asset/:assetId`,
        element: <AssetDetailPanel />,
        handle: {
          panel: { key: 'asset-detail', title: 'assets.menu.title' },
          header: {
            titleKey: 'assets.menu.title',
            icon: 'qrcode',
          },
          requiredPermission: Permissions.asset.read,
        },
      },
    ],
  },
  // Shared asset sub-routes mounted under the workspace asset path.
  // Mirrors assets/routes.tsx and activity/routes.tsx — all activity/routine/
  // file/edit sub-pages for a workspace asset are handled here.
  ...assetSubRoutes.map((r) => ({
    ...r,
    path: `${workspacePaths.root}/:workspaceId/asset/:assetId/${r.path}`,
    handle: {
      ...r.handle,
      header: { titleKey: 'admin.menu.workspaces', icon: 'group' },
      requiredPermission: Permissions.globalAdmin,
    },
  })),
];
